import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Badge from "@/components/base/Badge";
import ShareBar from "@/components/feature/ShareBar";
import { EmptyState, Spinner } from "@/components/base/StateViews";
import { getNewsById, getRelatedNews } from "@/utils/data";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const item = getNewsById(id);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 260);
    return () => window.clearTimeout(timer);
  }, [id]);

  useSeo({
    title: item ? `${item.title} - 影视资讯 - 亚洲天堂` : "资讯未找到 - 亚洲天堂",
    description: item?.summary,
    keywords: item ? [...item.tags, "影视资讯", "亚洲天堂"].join(",") : undefined,
  });

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-site px-4 py-16 md:px-6">
        <Spinner label="正在加载资讯…" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-site px-4 py-16 md:px-6">
        <EmptyState
          title="文章未找到"
          description="这篇资讯可能已被移除或链接有误，去首页看看其他精彩内容吧。"
          icon="ri-file-search-line"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedNews(item);

  return (
    <div className="animate-fade-in">
      <article className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
        <div className="mb-4">
          <Breadcrumb items={[{ label: "影视资讯" }, { label: item.title.slice(0, 8) + "…" }]} />
        </div>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-2xl font-bold leading-snug text-foreground-900 md:text-3xl">
              {item.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {item.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-newspaper-line" aria-hidden="true"></i>
                来源：{item.source}
              </span>
            </div>

            <div className="mt-5 overflow-hidden rounded-lg bg-background-200">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 影视资讯 - 亚洲天堂`}
                className="h-auto w-full object-cover object-top"
              />
            </div>

            <div className="mt-6 flex flex-col gap-4 text-base leading-loose text-foreground-700">
              {item.content.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {item.tags.map((tag) => (
                <Badge key={tag} tone="secondary">#{tag}</Badge>
              ))}
            </div>

            <div className="mt-6 border-t border-background-200 pt-5">
              <ShareBar title={item.title} />
            </div>

            {item.relatedWorkId ? (
              <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="mb-2 text-sm font-semibold text-foreground-900">相关作品</h3>
                <Link
                  to={`/detail/work/${item.relatedWorkId}`}
                  className="flex items-center gap-1.5 text-sm text-primary-700 transition-colors hover:text-accent-600"
                >
                  查看关联的影视作品详情
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            ) : null}
          </div>

          <aside className="lg:col-span-1">
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-900">相关资讯推荐</h2>
            <div className="flex flex-col gap-3">
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/detail/news/${rel.id}`}
                  className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200"
                >
                  <span className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={rel.cover}
                      alt={rel.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </span>
                  <span className="flex flex-1 flex-col">
                    <span className="line-clamp-2 text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-700">
                      {rel.title}
                    </span>
                    <span className="mt-auto text-xs text-foreground-400">{rel.date}</span>
                  </span>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </article>
    </div>
  );
}